/**
 * Etapa 1 — Situação do trabalho: onde e quem (processo, posto, setor, expostos) e as atividades do
 * processo com a classificação de risco. As atividades são gravadas na hora, pelo subrecurso; o posto
 * nasce ao "Salvar e continuar" (o Wizard faz o POST).
 */
import React, { useCallback, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { PrerequisiteBlock } from '../../../../../components/common';
import { CompanySelectField } from '../../../../../components/forms';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicActivity, ErgonomicWorkstationDetail } from '../../../../../types/ergonomicWorkstation';
import type { Department } from '../../../../../types/department';
import { MeasurementsLink } from '../ComfortCard';
import { ActivityDialog } from '../dialogs/ActivityDialog';
import { ConfirmDialog } from '../dialogs/ItemDialog';
import { ItemsBlock } from '../ItemsBlock';
import { useDepartmentContext, type DepartmentContextState } from '../useDepartmentContext';
import { useItems } from '../useItems';
import type { WizardStepProps } from '../Wizard.types';
import { errorMessage } from '../wizardValidation';
import { WorkstationField } from '../WorkstationField';

export interface SituationStepProps extends Omit<WizardStepProps, 'workstation'> {
  /** Nulo até a etapa ser salva pela primeira vez. */
  workstation: ErgonomicWorkstationDetail | null;
  departments: readonly Department[];
  departmentsLoading: boolean;
  /** Por que a lista de setores está vazia sem que falte cadastro (perfil sem leitura de setores). */
  departmentsBlocked?: string;
  departmentsFailed?: string;
  /** Pergunta a empresa do posto: só na criação, quando o cabeçalho não tem empresa ativa. */
  askCompany: boolean;
  companyLocked: boolean;
}

const fieldSx = { '& .MuiFormHelperText-root': { fontSize: '1rem' } };

/** "Este setor tem medição de conforto: sim/não", com palavra e com o caminho para as medições. */
const ComfortHint: React.FC<{ state: DepartmentContextState }> = ({ state }) => {
  if (state.status === 'idle') return <>Escolha o setor para ver se ele tem medição de conforto.</>;
  if (state.status === 'loading') return <>Conferindo as medições de conforto do setor...</>;
  if (state.status === 'error') {
    return <>Não foi possível conferir as medições do setor agora. Você pode seguir com o cadastro.</>;
  }
  return (
    <>
      Este setor tem medição de conforto: <b>{state.context.hasComfortMeasurement ? 'sim' : 'não'}</b>.{' '}
      <MeasurementsLink />
    </>
  );
};

/** A dica do Nº de expostos: a contagem do setor é só referência, quem decide é quem viu o posto. */
const ExposedHint: React.FC<{ state: DepartmentContextState }> = ({ state }) => (
  <>
    Quantas pessoas trabalham neste posto, pelo que você viu.
    {state.status === 'ready' && (
      <>
        <br />
        {`Funcionários ativos neste setor: ${state.context.activeEmployees} (só referência).`}
      </>
    )}
  </>
);

export const SituationStep: React.FC<SituationStepProps> = ({
  workstation,
  options,
  values,
  errors,
  canEdit,
  disabled,
  departments,
  departmentsLoading,
  departmentsBlocked,
  departmentsFailed,
  askCompany,
  companyLocked,
  onChange,
  onSaved,
}) => {
  const workstationId = workstation ? workstation.id : null;
  const departmentContext = useDepartmentContext(values.departmentId, values.companyId);
  const loadActivities = useCallback(
    async (): Promise<ErgonomicActivity[]> =>
      workstationId === null ? [] : ergonomicWorkstationService.getActivities(workstationId),
    [workstationId]
  );
  const activities = useItems(loadActivities, 'Não foi possível carregar as atividades.');
  const [dialog, setDialog] = useState<{ item: ErgonomicActivity | null } | null>(null);
  const [removing, setRemoving] = useState<ErgonomicActivity | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const classificationLabel = (value: string): string =>
    options?.riskClassifications.find((option) => option.value === value)?.label ?? value;

  const companyDepartments = values.companyId
    ? departments.filter((department) => String(department.companyId) === values.companyId)
    : departments;
  // Em edição o setor salvo precisa existir como opção mesmo se a lista não chegou (ou o perfil não a lê).
  const savedDepartmentMissing =
    workstation !== null &&
    workstation.departmentName !== null &&
    !companyDepartments.some((department) => String(department.id) === String(workstation.departmentId));
  const noDepartments =
    !askCompany || values.companyId !== ''
      ? !departmentsLoading && !departmentsBlocked && !departmentsFailed && companyDepartments.length === 0
      : false;

  const confirmRemoval = async () => {
    if (!removing || workstationId === null) return;
    try {
      await ergonomicWorkstationService.deleteActivity(workstationId, removing.id);
      setRemoving(null);
      await activities.reload();
      onSaved('Atividade removida.');
    } catch (err: unknown) {
      setRemoving(null);
      setRemoveError(errorMessage(err, 'Não foi possível remover a atividade.'));
    }
  };

  const closeDialogSaved = async (message: string) => {
    setDialog(null);
    await activities.reload();
    onSaved(message);
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Preencha pelo que você viu no local. Só processo, posto, setor e número de expostos são obrigatórios; o resto
        você completa agora ou depois.
      </Typography>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Onde e quem
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {askCompany && (
          <CompanySelectField
            grid={{ xs: 12, md: 6 }}
            required
            label="Empresa do posto"
            value={values.companyId}
            onChange={(companyId) => onChange({ companyId, departmentId: '' })}
            error={Boolean(errors.companyId)}
            helperText={
              errors.companyId ||
              (companyLocked ? 'A empresa não muda depois que o posto é salvo.' : 'O posto entra na análise desta empresa.')
            }
            disabled={disabled || companyLocked}
          />
        )}
        <WorkstationField
          field="process"
          label="Processo"
          required
          md={6}
          help="Ex.: Montagem de bancos"
          {...{ values, errors, onChange, canEdit, disabled }}
        />
        <WorkstationField
          field="name"
          label="Posto de trabalho"
          required
          md={6}
          help="Ex.: Início da montagem de suspensão"
          {...{ values, errors, onChange, canEdit, disabled }}
        />
        <Grid size={{ xs: 12, md: 4 }}>
          {departmentsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 56 }}>
              <CircularProgress size={24} aria-label="Carregando os setores" />
              <Typography variant="body1">Carregando os setores...</Typography>
            </Box>
          ) : (
            <TextField
              select
              fullWidth
              required
              label="Setor"
              value={values.departmentId}
              onChange={(event) => onChange({ departmentId: event.target.value })}
              error={Boolean(errors.departmentId)}
              helperText={
                errors.departmentId ||
                departmentsBlocked ||
                departmentsFailed || <ComfortHint state={departmentContext} />
              }
              disabled={disabled || !canEdit}
              sx={fieldSx}
              slotProps={{ formHelperText: { component: 'div' } }}
            >
              <MenuItem value="">Escolha o setor</MenuItem>
              {savedDepartmentMissing && workstation && (
                <MenuItem value={String(workstation.departmentId)}>{workstation.departmentName}</MenuItem>
              )}
              {companyDepartments.map((department) => (
                <MenuItem key={department.id} value={String(department.id)}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Grid>
        <WorkstationField
          field="workLocation"
          label="Local de trabalho"
          md={4}
          help="Ex.: Produção, linhas 1 e 2"
          {...{ values, errors, onChange, canEdit, disabled }}
        />
        <WorkstationField
          field="exposedCount"
          label="Nº de expostos"
          required
          md={4}
          inputMode="numeric"
          help={<ExposedHint state={departmentContext} />}
          {...{ values, errors, onChange, canEdit, disabled }}
        />
        {noDepartments && (
          <Grid size={{ xs: 12 }}>
            <PrerequisiteBlock
              title="Esta empresa ainda não tem setores"
              reason="Todo posto de trabalho fica dentro de um setor. Cadastre o setor onde o trabalho acontece e volte aqui. A página abre em outra aba para você não perder o que digitou."
              resolve={{
                label: 'Cadastrar um setor',
                permission: 'department.create',
                mode: 'navigate',
                route: '/departments/create',
                openInNewTab: true,
              }}
              deniedHint="Peça a quem administra a empresa para cadastrar um setor."
            />
          </Grid>
        )}
      </Grid>

      {workstation === null ? (
        <Box component="section">
          <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
            Atividades do processo
          </Typography>
          <Alert severity="info" sx={{ fontSize: '1rem' }}>
            As atividades entram assim que você salvar o posto: clique em "Salvar e continuar" e volte a esta etapa para
            descrevê-las, ou siga e complete depois.
          </Alert>
        </Box>
      ) : (
        <>
          {removeError && (
            <Alert severity="error" sx={{ fontSize: '1rem', mb: 2 }} onClose={() => setRemoveError(null)}>
              {removeError}
            </Alert>
          )}
          <ItemsBlock<ErgonomicActivity>
            title="Atividades do processo"
            description="Descreva cada atividade do posto e marque a classificação de risco dela."
            items={activities.items}
            loading={activities.loading}
            error={activities.error}
            onRetry={activities.reload}
            columns={[
              { id: 'description', label: 'Atividade', primary: true, render: (row) => row.description },
              {
                id: 'classifications',
                label: 'Classificação do risco',
                render: (row) =>
                  row.riskClassifications.length === 0 ? (
                    'Sem classificação'
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {row.riskClassifications.map((value) => (
                        <Chip
                          key={value}
                          size="small"
                          variant="outlined"
                          label={classificationLabel(value)}
                          sx={{ fontSize: '1rem', minHeight: 32 }}
                        />
                      ))}
                    </Box>
                  ),
              },
            ]}
            emptyState={{
              title: 'Nenhuma atividade descrita neste posto',
              description: 'Use "Adicionar atividade" para descrever o que a pessoa faz no posto, uma atividade por vez. Sem atividade, o posto não fica pronto.',
            }}
            addLabel="Adicionar atividade"
            itemName="atividade"
            itemLabel={(row) => row.description}
            canEdit={canEdit}
            onAdd={() => setDialog({ item: null })}
            onEdit={(row) => setDialog({ item: row })}
            onRemove={setRemoving}
          />
        </>
      )}

      {dialog && workstation && (
        <ActivityDialog
          workstationId={workstation.id}
          item={dialog.item}
          classifications={options?.riskClassifications ?? []}
          onSaved={closeDialogSaved}
          onClose={() => setDialog(null)}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Remover esta atividade?"
          message="Ela some do cadastro do posto e do documento."
          onConfirm={confirmRemoval}
          onCancel={() => setRemoving(null)}
        />
      )}
    </Box>
  );
};

export default SituationStep;
